import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Check, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react'
import type {
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
  ServerApiClient,
  ServiceLineRollup,
  SuggestedServiceLineRollup,
} from '../../../data/ServerApiClient'

const INK = '#111827'
const MUTED = '#6b7280'
const FAINT = '#9ca3af'
const HAIR = '#e5e7eb'
const TEAL = '#0F6E56'

type NodeOption = { id: string; label: string }

function nodeLabel(row: QuickBooksFpaStatementRow): string {
  const num = row.account_number ? `${row.account_number} · ` : ''
  return `${num}${row.label}`
}

function extractNodeOptions(statement: QuickBooksFpaStatementRender | null): { income: NodeOption[]; cogs: NodeOption[] } {
  if (!statement) return { income: [], cogs: [] }
  const income: NodeOption[] = []
  const cogs: NodeOption[] = []
  for (const row of statement.rows) {
    if (!row.node_id || !row.account_number) continue
    const type = String(row.qbo_account_type || '').toLowerCase()
    const cls = String(row.qbo_classification || '').toLowerCase()
    const label = String(row.label || '').toLowerCase()
    const isCogs = type.includes('cost of goods') || type.includes('cost of sales') || type.includes('cogs') || /cogs|cost of (goods|sales)/.test(label)
    const isIncome = !isCogs && (cls === 'revenue' || cls === 'income' || type.includes('income') || type.includes('revenue'))
    if (isCogs) cogs.push({ id: row.node_id, label: nodeLabel(row) })
    else if (isIncome) income.push({ id: row.node_id, label: nodeLabel(row) })
  }
  return { income, cogs }
}

export type ServiceLineConfigDialogProps = {
  open: boolean
  appApi: ServerApiClient
  layoutId: string
  statement: QuickBooksFpaStatementRender | null
  rollups: ServiceLineRollup[]
  suggestions: SuggestedServiceLineRollup[]
  startInSuggestions?: boolean
  detecting?: boolean
  onDetect: () => void
  onClose: () => void
  onChanged: () => void
}

export function ServiceLineConfigDialog({
  open,
  appApi,
  layoutId,
  statement,
  rollups,
  suggestions,
  startInSuggestions = false,
  detecting = false,
  onDetect,
  onClose,
  onChanged,
}: ServiceLineConfigDialogProps) {
  const options = useMemo(() => extractNodeOptions(statement), [statement])
  const optionLabel = useMemo(() => {
    const map = new Map<string, string>()
    for (const o of [...options.income, ...options.cogs]) map.set(o.id, o.label)
    return map
  }, [options])

  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [name, setName] = useState('')
  const [incomeIds, setIncomeIds] = useState<Set<string>>(new Set())
  const [cogsIds, setCogsIds] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) {
      setEditing(null)
      setError('')
    }
  }, [open])

  if (!open) return null

  const resetForm = () => {
    setEditing(null)
    setName('')
    setIncomeIds(new Set())
    setCogsIds(new Set())
  }

  const beginCreate = () => {
    setEditing('new')
    setName('')
    setIncomeIds(new Set())
    setCogsIds(new Set())
    setError('')
  }

  const beginEdit = (rollup: ServiceLineRollup) => {
    setEditing(rollup.id)
    setName(rollup.name)
    setIncomeIds(new Set(rollup.income_node_ids))
    setCogsIds(new Set(rollup.cogs_node_ids))
    setError('')
  }

  const beginFromSuggestion = (s: SuggestedServiceLineRollup) => {
    setEditing('new')
    setName(s.name)
    setIncomeIds(new Set(s.income_node_ids))
    setCogsIds(new Set(s.cogs_node_ids))
    setError('')
  }

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setter(next)
  }

  const save = async () => {
    if (!name.trim()) { setError('Give the rollup a name.'); return }
    if (incomeIds.size === 0) { setError('Select at least one income account.'); return }
    if (cogsIds.size === 0) { setError('Select at least one COGS account.'); return }
    setBusy(true)
    setError('')
    try {
      const payload = { name: name.trim(), income_node_ids: [...incomeIds], cogs_node_ids: [...cogsIds] }
      if (editing === 'new') await appApi.createServiceLineRollup(layoutId, payload)
      else if (editing) await appApi.updateServiceLineRollup(layoutId, editing, payload)
      resetForm()
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the rollup.')
    } finally {
      setBusy(false)
    }
  }

  const applySuggestion = async (s: SuggestedServiceLineRollup) => {
    setBusy(true)
    setError('')
    try {
      await appApi.createServiceLineRollup(layoutId, {
        name: s.name,
        income_node_ids: s.income_node_ids,
        cogs_node_ids: s.cogs_node_ids,
        auto_detected: true,
        confidence: s.confidence,
        signals: s.signals,
      })
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not apply the suggestion.')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (rollup: ServiceLineRollup) => {
    setBusy(true)
    setError('')
    try {
      await appApi.deleteServiceLineRollup(layoutId, rollup.id)
      if (editing === rollup.id) resetForm()
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the rollup.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={overlay} onClick={() => { if (!busy) onClose() }}>
      <div role="dialog" aria-modal="true" style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: INK }}>Configure service lines</div>
          <button type="button" className="btn" onClick={onClose} aria-label="Close" style={{ padding: 6 }}><X size={16} /></button>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>
            Pair each revenue line with its directly-attributable COGS. Contribution margin is computed per line; shared costs stay unallocated.
          </div>
          <button type="button" className="btn" disabled={busy || detecting} onClick={onDetect} title="Auto-detect pairings from the chart of accounts"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Sparkles size={14} aria-hidden="true" /> {detecting ? 'Detecting…' : 'Detect lines'}
          </button>
        </div>

        {error ? <div style={{ fontSize: 13, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px' }}>{error}</div> : null}

        {/* Existing rollups */}
        <div>
          <div style={sectionLabel}>Rollups</div>
          {rollups.length === 0 ? (
            <div style={{ fontSize: 13, color: FAINT, padding: '6px 0' }}>None yet.</div>
          ) : (
            rollups.map((r) => (
              <div key={r.id} style={listRow}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, color: INK, fontSize: 14 }}>
                    {r.name}{r.auto_detected ? <span style={autoTag}>auto</span> : null}
                  </div>
                  <div style={{ fontSize: 12, color: FAINT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.income_node_ids.map((id) => optionLabel.get(id) || id).join(', ')}
                    {' → '}
                    {r.cogs_node_ids.map((id) => optionLabel.get(id) || id).join(', ')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button type="button" className="btn" disabled={busy} onClick={() => beginEdit(r)} aria-label="Edit" style={iconBtn}><Pencil size={14} /></button>
                  <button type="button" className="btn" disabled={busy} onClick={() => void remove(r)} aria-label="Delete" style={iconBtn}><Trash2 size={14} /></button>
                </div>
              </div>
            ))
          )}
          {editing === null ? (
            <button type="button" className="btn" disabled={busy} onClick={beginCreate} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              <Plus size={14} /> Add a service line
            </button>
          ) : null}
        </div>

        {/* Create / edit form */}
        {editing !== null ? (
          <div style={{ border: `1px solid ${HAIR}`, borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: INK }}>{editing === 'new' ? 'New service line' : 'Edit service line'}</div>
            <label style={{ fontSize: 12, color: MUTED, display: 'flex', flexDirection: 'column', gap: 4 }}>
              Name
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. AOX" disabled={busy}
                style={{ border: `1px solid ${HAIR}`, borderRadius: 8, padding: '7px 10px', fontSize: 14 }} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <AccountPicker title="Income accounts" options={options.income} selected={incomeIds} onToggle={(id) => toggle(incomeIds, setIncomeIds, id)} disabled={busy} />
              <AccountPicker title="COGS accounts" options={options.cogs} selected={cogsIds} onToggle={(id) => toggle(cogsIds, setCogsIds, id)} disabled={busy} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn" disabled={busy} onClick={resetForm}>Cancel</button>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void save()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Check size={14} /> {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : null}

        {/* Suggestions */}
        {suggestions.length > 0 ? (
          <div>
            <div style={sectionLabel}><Sparkles size={13} aria-hidden="true" style={{ color: TEAL, verticalAlign: -2 }} /> Suggested pairings</div>
            {suggestions.map((s, i) => (
              <div key={`${s.name}-${i}`} style={listRow}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, color: INK, fontSize: 14 }}>
                    {s.name}<span style={{ ...autoTag, background: '#eef2ff', color: '#3730a3' }}>{Math.round(s.confidence * 100)}%</span>
                  </div>
                  <div style={{ fontSize: 12, color: FAINT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.income_node_ids.map((id) => optionLabel.get(id) || id).join(', ')}{' → '}
                    {s.cogs_node_ids.map((id) => optionLabel.get(id) || id).join(', ')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button type="button" className="btn" disabled={busy} onClick={() => beginFromSuggestion(s)} style={{ fontSize: 12 }}>Review</button>
                  <button type="button" className="btn primary" disabled={busy} onClick={() => void applySuggestion(s)} style={{ fontSize: 12 }}>Apply</button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function AccountPicker({ title, options, selected, onToggle, disabled }: { title: string; options: NodeOption[]; selected: Set<string>; onToggle: (id: string) => void; disabled?: boolean }) {
  return (
    <div style={{ border: `1px solid ${HAIR}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: MUTED, padding: '6px 10px', background: '#f6f7f9', borderBottom: `1px solid ${HAIR}` }}>{title}</div>
      <div style={{ maxHeight: 160, overflow: 'auto' }}>
        {options.length === 0 ? (
          <div style={{ fontSize: 12, color: FAINT, padding: '8px 10px' }}>No accounts found.</div>
        ) : (
          options.map((o) => (
            <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', fontSize: 13, cursor: disabled ? 'default' : 'pointer', borderBottom: `1px solid #f3f4f6` }}>
              <input type="checkbox" checked={selected.has(o.id)} onChange={() => onToggle(o.id)} disabled={disabled} />
              <span style={{ color: INK }}>{o.label}</span>
            </label>
          ))
        )}
      </div>
    </div>
  )
}

const overlay: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 40000, background: 'rgba(17, 24, 39, 0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
}
const card: CSSProperties = {
  width: 'min(720px, 96vw)', maxHeight: 'min(86vh, 820px)', overflow: 'auto', borderRadius: 12, border: `1px solid #d1d5db`, background: '#fff', boxShadow: '0 24px 48px rgba(0,0,0,0.18)', padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
}
const sectionLabel: CSSProperties = { fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.04em', color: FAINT, marginBottom: 4 }
const listRow: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: `1px solid ${HAIR}` }
const iconBtn: CSSProperties = { padding: 6 }
const autoTag: CSSProperties = { fontSize: 10.5, fontWeight: 500, background: '#e1f5ee', color: TEAL, borderRadius: 999, padding: '1px 7px', marginLeft: 8 }

export default ServiceLineConfigDialog
